/**
 * Public client for the v2 applicant funnel endpoints on Glen.
 *
 * Kept as a small standalone module (not bolted onto glencoco-api.ts)
 * so the new funnel can evolve independently. Uses native fetch
 * rather than the axios instance because the responses are small and
 * we want fewer dependencies in the critical funnel path.
 */

export interface PreApplyAnswer {
  question_id: string;
  answer: boolean;
}

export interface PreApplyRequest {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  answers: PreApplyAnswer[];
  turnstile_token?: string;
}

export interface PreApplyResponse {
  status_code?: number;
  error_code?: number;
  error_str?: string;
  attempt_id?: string;
  qualified?: boolean;
  failed_question_id?: string;
  failure_reason?: string;
  retry_after_seconds?: number;
}

export interface ApplicationScript {
  id: string;
  label: string;
  product_service_description: string;
  target_persona: string;
  script_text: string;
  recordings: Array<{
    id: string;
    audio_s3_key: string;
    label?: string;
  }>;
}

export interface ListApplicationScriptsResponse {
  status_code?: number;
  error_str?: string;
  scripts: ApplicationScript[];
}

export function getApiBase(): string {
  const raw = process.env.NEXT_PUBLIC_API_DOMAIN || "";
  // Landing app env var carries a "/landing" prefix that the ALB strips,
  // and sometimes also a trailing "/public" that the legacy axios client
  // (glencoco-api.ts) absorbs into its baseURL. The applicant-api fetch
  // paths hardcode "/public/v1/..." themselves, so we must strip both to
  // avoid the doubled "/public/public/v1/..." path that 404s on Glen.
  return raw.replace(/\/landing(?=\/|$)/, "").replace(/\/public$/, "");
}

export async function submitPreApplication(
  body: PreApplyRequest,
  opts?: { signal?: AbortSignal }
): Promise<PreApplyResponse> {
  const base = getApiBase();
  if (!base) {
    throw new Error(
      "NEXT_PUBLIC_API_DOMAIN is not configured; cannot submit pre-application"
    );
  }
  const res = await fetch(`${base}/public/v1/applicant/pre-apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal: opts?.signal,
  });
  // Glen returns JSON for both success and error responses; let the
  // caller decide what to do with non-2xx status. The response shape
  // is consistent regardless.
  const data: PreApplyResponse = await res.json().catch(() => ({}));
  if (!res.ok && !data.error_str) {
    data.error_str = `HTTP ${res.status}`;
  }
  return data;
}

export async function listApplicationScripts(): Promise<ListApplicationScriptsResponse> {
  const base = getApiBase();
  if (!base) {
    return { scripts: [] };
  }
  const res = await fetch(`${base}/public/v1/applicant/scripts`);
  return (await res
    .json()
    .catch(() => ({ scripts: [] }))) as ListApplicationScriptsResponse;
}

/**
 * Discriminated-union result for the applicant audio upload-URL request.
 *
 * Backend response (V1GetCVUploadURLResponse, reused from the ATS flow)
 * uses optional fields, but every caller cares about exactly one of two
 * things: a usable {upload_url, s3_key} pair, or a reason it failed. We
 * normalize to a discriminated union so the type system forces callers
 * to check `ok` before reading the URL, instead of relying on a runtime
 * null-check on the optional fields.
 */
export type ApplicantAudioUploadURLResult =
  | {
      ok: true;
      upload_url: string;
      s3_key: string;
      status_code?: number;
    }
  | {
      ok: false;
      error_str: string;
      error_code?: number;
      status_code?: number;
    };

/**
 * Allow-list of audio MIME types the backend understands. Anything else
 * is normalized to audio/webm both client- and server-side. The codec
 * parameter (`;codecs=opus`) is stripped before matching.
 */
export function normalizeAudioMime(raw: string): string {
  const cleaned = (raw || "").toLowerCase().split(";")[0].trim();
  switch (cleaned) {
    case "audio/mp4":
    case "audio/m4a":
      return "audio/mp4";
    case "audio/ogg":
      return "audio/ogg";
    case "audio/wav":
    case "audio/x-wav":
      return "audio/wav";
    case "audio/webm":
    case "":
      return "audio/webm";
    default:
      return "audio/webm";
  }
}

/**
 * Requests a short-lived presigned PUT URL for uploading the applicant's
 * script-reading recording to S3. Mirrors the legacy ATS audio upload
 * flow used by /openings/application; the only differences are the route
 * prefix and the path scoping (pre_application_attempt_id instead of
 * campaign_id).
 *
 * Each call generates a fresh S3 key on the backend, so re-recording is
 * supported by just calling this function again with the new blob's
 * content length. The caller is responsible for stashing the returned
 * s3_key on form state and sending it on the final /apply submit.
 *
 * The contentType MUST be the blob's MIME (post-normalization) so the
 * backend signs the URL with the matching content-type header. Safari
 * records audio/mp4 not audio/webm, and a mismatch here causes the
 * downstream PUT to fail with an S3 signature error.
 */
export async function getApplicantAudioUploadURL(
  attemptId: string | null,
  contentLength: number,
  contentType: string
): Promise<ApplicantAudioUploadURLResult> {
  const base = getApiBase();
  if (!base) {
    return {
      ok: false,
      error_str:
        "NEXT_PUBLIC_API_DOMAIN is not configured; cannot request upload URL",
    };
  }
  if (!attemptId) {
    return { ok: false, error_str: "attempt_id required" };
  }

  const path = `${base}/public/v1/applicant/${encodeURIComponent(
    attemptId
  )}/audio_recording_upload_url`;

  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      content_length: contentLength,
      content_type: contentType,
    }),
  });
  const raw: {
    status_code?: number;
    error_code?: number;
    error_str?: string;
    upload_url?: string;
    s3_key?: string;
  } = await res.json().catch(() => ({}));

  if (res.ok && raw.upload_url && raw.s3_key) {
    return {
      ok: true,
      upload_url: raw.upload_url,
      s3_key: raw.s3_key,
      status_code: raw.status_code,
    };
  }
  return {
    ok: false,
    error_str: raw.error_str || `HTTP ${res.status}`,
    error_code: raw.error_code,
    status_code: raw.status_code,
  };
}

/**
 * PUTs an audio Blob to the S3 presigned URL. Content-Type MUST match
 * what was passed to getApplicantAudioUploadURL (otherwise S3 rejects
 * the signed request with a SignatureDoesNotMatch error).
 *
 * SEC-4: x-amz-acl header removed. Resumes + audio land in the PRIVATE
 * S3 bucket now; the public-read ACL on the legacy path was a PII
 * exposure. The backend's presigned URL no longer signs an ACL header.
 *
 * Returns { ok: true } on success or { ok: false, status, statusText }
 * on failure. Callers should surface a clear error to the applicant —
 * the previous version returned boolean and swallowed the HTTP status,
 * which caused the "503 from S3 but UI says Saved" bug found during E2E.
 */
export async function putApplicantAudioToS3(
  uploadUrl: string,
  audioBlob: Blob,
  contentType: string
): Promise<{ ok: true } | { ok: false; status: number; statusText: string }> {
  try {
    const res = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: audioBlob,
    });
    if (res.ok) {
      return { ok: true };
    }
    return { ok: false, status: res.status, statusText: res.statusText };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      statusText: err instanceof Error ? err.message : "network error",
    };
  }
}

/**
 * Allowed MIME types for the resume upload. Mirrors the backend handler
 * which accepts PDF (default) or DOCX; anything else falls back to PDF.
 * Exported so the form can use a consistent allowlist for the file
 * picker's accept attribute and content-type negotiation.
 */
export const RESUME_PDF_MIME = "application/pdf";
export const RESUME_DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/**
 * Discriminated-union result for the applicant resume upload-URL
 * request. Mirrors ApplicantAudioUploadURLResult — see that type for
 * why the response is normalized away from the backend's optional-field
 * shape.
 */
export type ApplicantResumeUploadURLResult =
  | {
      ok: true;
      upload_url: string;
      s3_key: string;
      status_code?: number;
    }
  | {
      ok: false;
      error_str: string;
      error_code?: number;
      status_code?: number;
    };

/**
 * Requests a presigned PUT URL for the applicant's resume. Same backend
 * pattern as the audio upload: scoped by pre_application_attempt_id,
 * accepts content_length, plus an optional content_type to support DOCX.
 *
 * The content type is passed through to the backend which signs the URL
 * with the matching MIME header. Frontend MUST then PUT with the same
 * Content-Type header or S3 rejects the upload with a signature error.
 */
export async function getApplicantResumeUploadURL(
  attemptId: string | null,
  contentLength: number,
  contentType: string
): Promise<ApplicantResumeUploadURLResult> {
  const base = getApiBase();
  if (!base) {
    return {
      ok: false,
      error_str:
        "NEXT_PUBLIC_API_DOMAIN is not configured; cannot request upload URL",
    };
  }
  if (!attemptId) {
    return { ok: false, error_str: "attempt_id required" };
  }

  const path = `${base}/public/v1/applicant/${encodeURIComponent(
    attemptId
  )}/resume_upload_url`;

  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      content_length: contentLength,
      content_type: contentType,
    }),
  });
  const raw: {
    status_code?: number;
    error_code?: number;
    error_str?: string;
    upload_url?: string;
    s3_key?: string;
  } = await res.json().catch(() => ({}));

  if (res.ok && raw.upload_url && raw.s3_key) {
    return {
      ok: true,
      upload_url: raw.upload_url,
      s3_key: raw.s3_key,
      status_code: raw.status_code,
    };
  }
  return {
    ok: false,
    error_str: raw.error_str || `HTTP ${res.status}`,
    error_code: raw.error_code,
    status_code: raw.status_code,
  };
}

/**
 * PUTs the resume File to S3 using the presigned URL. Content-Type MUST
 * match what was passed to getApplicantResumeUploadURL or S3 rejects the
 * signed request.
 *
 * SEC-4: x-amz-acl header removed. The resume bucket is private; the
 * backend's signed URL no longer signs an ACL header.
 *
 * Returns { ok: true } on success or { ok: false, status, statusText }
 * on failure so callers can show a real error instead of silently
 * recording a missing-resume submission.
 */
export async function putApplicantResumeToS3(
  uploadUrl: string,
  file: File,
  contentType: string
): Promise<{ ok: true } | { ok: false; status: number; statusText: string }> {
  try {
    const res = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: file,
    });
    if (res.ok) {
      return { ok: true };
    }
    return { ok: false, status: res.status, statusText: res.statusText };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      statusText: err instanceof Error ? err.message : "network error",
    };
  }
}

export interface ApplicantApplyRequest {
  pre_application_attempt_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  country?: string;
  years_of_experience?: number;
  linkedin_url?: string;
  source?: string;
  source_other?: string;
  resume_s3_key?: string;
  audio_s3_key?: string;
  selected_script_id?: string;
}

export interface ApplicantApplyResponse {
  status_code?: number;
  error_code?: number;
  error_str?: string;
  user_id?: string;
  application_status?: string;
  // Sanitized client-facing message backend sets for known errors
  // (email_mismatch, invalid_script, etc.). Prefer this over error_str
  // when surfacing the failure to the applicant — error_str carries the
  // raw Go error label like "email_mismatch", failure_reason carries a
  // pre-written sentence like "Submitted email does not match
  // pre-application.".
  failure_reason?: string;
}

/**
 * Submits the full applicant application. Pairs with /apply/form's
 * "Submit application" button.
 *
 * The pre_application_attempt_id field is required by the backend
 * (links the v2 pre-app row to the new user record). Pull it from
 * sessionStorage under the key written by /apply/qualify.
 *
 * country/source values must match the backend's ReferralSource and
 * Country enum stringer outputs ("UnitedStates", "YouTubeShorts",
 * etc.); the dropdown options in
 * components/modules/apply/personal-info-options.ts are already
 * structured that way.
 */
export async function submitApplication(
  body: ApplicantApplyRequest
): Promise<ApplicantApplyResponse> {
  const base = getApiBase();
  if (!base) {
    throw new Error(
      "NEXT_PUBLIC_API_DOMAIN is not configured; cannot submit application"
    );
  }
  const res = await fetch(`${base}/public/v1/applicant/apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data: ApplicantApplyResponse = await res.json().catch(() => ({}));
  if (!res.ok && !data.error_str) {
    data.error_str = `HTTP ${res.status}`;
  }
  return data;
}
